import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { g, p, del, errMsg } from "@/lib/api";
import { usePlan } from "@/context/PlanContext";
import { PremiumLock } from "@/components/PremiumLock";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Boxes, ChefHat, History, Minus } from "lucide-react";

export default function Magazzino() {
  const { has } = usePlan();
  const [tab, setTab] = useState("giacenze");
  const [rows, setRows] = useState([]);
  const [menu, setMenu] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [moves, setMoves] = useState([]);
  const [form, setForm] = useState({ name: "", unit: "kg", qty: 0, min_qty: 0, supplier: "" });
  const [moveFor, setMoveFor] = useState(null);
  const [moveVal, setMoveVal] = useState({ delta: 1, reason: "" });
  const [recipeFor, setRecipeFor] = useState(null);
  const [lines, setLines] = useState([]);

  const load = useCallback(async () => {
    if (!has("inventory")) return;
    try {
      const [inv, mv, rc, mn] = await Promise.all([g("/inventory"), g("/stock/moves"), g("/recipes"), g("/menu")]);
      setRows(inv);
      setMoves(mv);
      setRecipes(rc);
      setMenu(mn);
    } catch (e) {
      console.warn("Magazzino non disponibile (piano o rete):", e?.response?.status || e?.message);
    }
  }, [has]);
  useEffect(() => {
    load();
  }, [load]);

  if (!has("inventory"))
    return (
      <PremiumLock
        title="Gestione magazzino"
        description="Giacenze, soglie minime, ricette con scarico automatico all'invio della comanda e storico dei movimenti."
        needed="pro"
        message="La gestione magazzino è inclusa nel piano Pro"
        preview={
          <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-3">
            {[["Articoli", "6"], ["Sotto soglia", "2"], ["Ricette", "4"]].map(([l, v]) => (
              <Card key={l} className="p-5">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{l}</p>
                <p className="font-num mt-3 text-2xl font-bold">{v}</p>
              </Card>
            ))}
          </div>
        }
      />
    );

  const low = rows.filter((r) => r.qty <= r.min_qty).length;
  const recipeOf = (mid) => recipes.find((r) => r.menu_item_id === mid);

  const doMove = async () => {
    try {
      await p(`/inventory/${moveFor.id}/move`, { delta: Number(moveVal.delta), reason: moveVal.reason, by: "Magazzino" });
      setMoveFor(null);
      toast.success("Movimento registrato");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveRecipe = async () => {
    try {
      const clean = lines.filter((l) => l.stock_id && Number(l.qty) > 0).map((l) => ({ stock_id: l.stock_id, qty: Number(l.qty) }));
      await p("/recipes", { menu_item_id: recipeFor.id, lines: clean });
      setRecipeFor(null);
      toast.success("Ricetta salvata: lo scarico sarà automatico all'invio");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="fade-up space-y-5" data-testid="magazzino-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">Piano Pro</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Magazzino</h1>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[["Articoli", rows.length], ["Sotto soglia", low], ["Ricette collegate", recipes.length]].map(([l, v]) => (
          <Card key={l} className="p-5" data-testid={`stock-kpi-${l.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="flex items-start justify-between">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{l}</p>
              <Boxes className="h-4 w-4 text-slate-400" />
            </div>
            <p className="font-num mt-3 text-2xl font-bold">{v}</p>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {[["giacenze", "Giacenze"], ["ricette", "Ricette e scarico"], ["movimenti", "Storico movimenti"]].map(([k, l]) => (
          <button
            key={k}
            data-testid={`magazzino-tab-${k}`}
            onClick={() => setTab(k)}
            className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${tab === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "giacenze" && (
        <>
          <Card className="flex flex-wrap items-end gap-2 p-5" data-testid="add-stock-bar">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Articolo</p>
              <Input className="mt-1 h-10 w-48" placeholder="Es. Farina 00" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="stock-name-input" />
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Unità</p>
              <Input className="mt-1 h-10 w-20" placeholder="kg" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="stock-unit-input" />
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Quanto ne hai</p>
              <Input className="mt-1 h-10 w-24" type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="stock-qty-input" />
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Avvisami sotto</p>
              <Input className="mt-1 h-10 w-24" type="number" value={form.min_qty} onChange={(e) => setForm({ ...form, min_qty: e.target.value })} data-testid="stock-min-input" />
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Fornitore</p>
              <Input className="mt-1 h-10 w-40" placeholder="Chi te lo porta" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} data-testid="stock-supplier-input" />
            </div>
            <Button
              data-testid="add-stock-btn"
              onClick={async () => {
                if (!form.name.trim()) return toast.error("Inserisci il nome dell'articolo");
                await p("/inventory", { ...form, qty: Number(form.qty) || 0, min_qty: Number(form.min_qty) || 0 });
                setForm({ name: "", unit: "kg", qty: 0, min_qty: 0, supplier: "" });
                toast.success("Articolo aggiunto");
                load();
              }}
            >
              <Plus className="mr-1.5 h-4 w-4" /> Aggiungi
            </Button>
          </Card>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {rows.map((r) => (
              <Card key={r.id} className={`p-5 ${r.qty <= r.min_qty ? "border-2 border-rose-400 bg-rose-50" : ""}`} data-testid={`stock-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{r.name}</p>
                    <p className="text-xs text-slate-500">{r.supplier || "—"} · soglia {r.min_qty} {r.unit}</p>
                  </div>
                  <span className={`font-num rounded-full px-2.5 py-1 text-xs font-bold ${r.qty <= r.min_qty ? "bg-rose-200 text-rose-800" : "bg-emerald-100 text-emerald-700"}`}>
                    {r.qty} {r.unit}
                  </span>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <Button size="sm" variant="outline" data-testid={`stock-load-${r.id}`} onClick={() => { setMoveFor(r); setMoveVal({ delta: 1, reason: "Carico da fornitore" }); }}>
                    <Plus className="mr-1 h-3.5 w-3.5" /> Carico
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`stock-unload-${r.id}`} onClick={() => { setMoveFor(r); setMoveVal({ delta: -1, reason: "Scarico manuale" }); }}>
                    <Minus className="mr-1 h-3.5 w-3.5" /> Scarico
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="ml-auto text-rose-600"
                    data-testid={`stock-delete-${r.id}`}
                    onClick={async () => {
                      await del(`/inventory/${r.id}`);
                      toast.success("Articolo eliminato");
                      load();
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {tab === "ricette" && (
        <Card className="p-5" data-testid="recipes-panel">
          <p className="flex items-center gap-2 text-sm text-slate-500">
            <ChefHat className="h-4 w-4" /> Collega gli ingredienti a ogni piatto: all'invio della comanda il magazzino si scarica da solo.
          </p>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {menu.map((m) => {
              const r = recipeOf(m.id);
              return (
                <div key={m.id} className="rounded-md border border-slate-200 p-3" data-testid={`recipe-card-${m.id}`}>
                  <p className="text-sm font-semibold">{m.name}</p>
                  <p className="text-[11px] text-slate-500">{m.category} · {m.department}</p>
                  {r?.lines?.length > 0 ? (
                    <div className="mt-2 space-y-0.5">
                      {r.lines.map((l) => {
                        const s = rows.find((x) => x.id === l.stock_id);
                        return (
                          <p key={l.stock_id} className="font-num text-[11px] text-slate-600">
                            {s?.name || "—"}: {l.qty} {s?.unit || ""}
                          </p>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="mt-2 text-[11px] text-slate-400">Nessuna ricetta collegata</p>
                  )}
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`edit-recipe-${m.id}`}
                      onClick={() => {
                        setRecipeFor(m);
                        setLines(r?.lines?.length ? r.lines.map((l) => ({ ...l })) : [{ stock_id: "", qty: 1 }]);
                      }}
                    >
                      {r ? "Modifica ricetta" : "Collega ingredienti"}
                    </Button>
                    {r && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-rose-600"
                        data-testid={`del-recipe-${m.id}`}
                        onClick={async () => {
                          await del(`/recipes/${m.id}`);
                          toast.success("Ricetta rimossa");
                          load();
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {tab === "movimenti" && (
        <Card className="p-5" data-testid="moves-panel">
          <p className="flex items-center gap-2 text-sm text-slate-500">
            <History className="h-4 w-4" /> Carichi, scarichi manuali e scarichi automatici dalle comande.
          </p>
          {moves.length === 0 && <p className="mt-3 text-sm text-slate-500">Nessun movimento registrato.</p>}
          <div className="mt-3 divide-y divide-slate-100">
            {moves.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 py-2 text-sm" data-testid={`move-${m.id}`}>
                <div className="min-w-0">
                  <p className="truncate font-medium">{m.name}</p>
                  <p className="text-[11px] text-slate-500">
                    {m.reason} · {m.by} · {new Date(m.at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className={`font-num font-bold ${m.delta < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                    {m.delta > 0 ? "+" : ""}{m.delta} {m.unit}
                  </p>
                  <p className="font-num text-[11px] text-slate-500">resta {m.qty_after} {m.unit}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Dialog open={!!moveFor} onOpenChange={(v) => !v && setMoveFor(null)}>
        <DialogContent data-testid="move-dialog">
          <DialogHeader>
            <DialogTitle>{moveVal.delta >= 0 ? "Carico" : "Scarico"} · {moveFor?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input type="number" value={moveVal.delta} onChange={(e) => setMoveVal({ ...moveVal, delta: e.target.value })} data-testid="move-delta-input" />
            <Input placeholder="Motivo" value={moveVal.reason} onChange={(e) => setMoveVal({ ...moveVal, reason: e.target.value })} data-testid="move-reason-input" />
            <Button className="h-11 w-full" onClick={doMove} data-testid="save-move-btn">
              Registra movimento
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!recipeFor} onOpenChange={(v) => !v && setRecipeFor(null)}>
        <DialogContent data-testid="recipe-dialog">
          <DialogHeader>
            <DialogTitle>Ricetta di {recipeFor?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {lines.map((l, idx) => (
              <div key={`${l.stock_id || "vuoto"}-${idx}`} className="flex items-center gap-2" data-testid={`recipe-line-${idx}`}>
                <select
                  className="h-10 min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-2 text-sm"
                  value={l.stock_id}
                  onChange={(e) => setLines(lines.map((x, i) => (i === idx ? { ...x, stock_id: e.target.value } : x)))}
                  data-testid={`recipe-stock-${idx}`}
                >
                  <option value="">Ingrediente…</option>
                  {rows.map((s) => (
                    <option key={s.id} value={s.id}>{s.name} ({s.unit})</option>
                  ))}
                </select>
                <Input
                  className="h-10 w-24"
                  type="number"
                  step="0.01"
                  value={l.qty}
                  onChange={(e) => setLines(lines.map((x, i) => (i === idx ? { ...x, qty: e.target.value } : x)))}
                  data-testid={`recipe-qty-${idx}`}
                />
                <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setLines(lines.filter((_, i) => i !== idx))} data-testid={`recipe-del-line-${idx}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => setLines([...lines, { stock_id: "", qty: 1 }])} data-testid="recipe-add-line-btn">
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Aggiungi ingrediente
            </Button>
            <Button className="h-11 w-full" onClick={saveRecipe} data-testid="save-recipe-btn">
              Salva ricetta
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
